import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { ProjectId } from 'api/models';
import { Modal } from 'components/atoms/modal';
import { ToastDescription, toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { useUnarchiveProjectMutation } from 'queries/project';
import { getProjectPlanUrl } from 'utils/urls';

interface Props {
  isVisible: boolean;
  hideUnarchiveSuccessAction?: boolean;
  projectId: number;
  projectName: string;
  redirectTo?: string;
  closeModal: () => void;
  onUnarchiveSuccess?: () => void;
}

export const UnarchiveProjectModal: FC<Props> = ({
  isVisible,
  hideUnarchiveSuccessAction,
  projectId,
  projectName,
  redirectTo,
  closeModal,
  onUnarchiveSuccess,
}) => {
  const history = useHistory();

  const { mutate: unarchiveProjMutate, isLoading: isunArchiving } =
    useUnarchiveProjectMutation();

  const unarchiveTitle = useTranslation('project.action.unarchive.title');
  const unarchiveButtonText = useTranslation('unarchive');
  const unarchiveSuccessMessage = useTranslation(
    'project.action.unarchive.modal.unarchive.message',
    { projectName },
  );

  const unarchiveErrorTitle = useTranslation('error_action');
  const unarchiveErrorMessage = useTranslation('error');
  const cta = useTranslation('project.action.unarchive.modal.unarchive.cta');

  const navigateToProject = () => {
    history.push(redirectTo ?? getProjectPlanUrl(ProjectId(projectId)));

    // Force reload as the plan page is handled by error page now.
    return history.go(0);
  };

  const handleProjectUnarchive = () => {
    unarchiveProjMutate(
      { projectId },
      {
        onSuccess: () => {
          toastService({
            title: unarchiveTitle,
            message: (
              <ToastDescription
                buttonText={cta}
                message={unarchiveSuccessMessage}
                onClick={
                  !hideUnarchiveSuccessAction ? navigateToProject : undefined
                }
              />
            ),
          }).success();
          closeModal();
          if (onUnarchiveSuccess) {
            window.setTimeout(() => {
              onUnarchiveSuccess();
            }, 1000);
          }
        },
        onError: () => {
          toastService({
            title: unarchiveErrorTitle,
            message: unarchiveErrorMessage,
          }).error();
        },
      },
    );
  };

  return (
    <Modal
      visible={isVisible}
      title={unarchiveTitle}
      okText={unarchiveButtonText}
      okButtonProps={{
        type: 'primary',
        name: 'project.action.unarchive.modal.confirm',
      }}
      onOk={handleProjectUnarchive}
      onCancel={closeModal}
      confirmLoading={isunArchiving}
    >
      <FormattedMessage
        id="project.action.unarchive.modal.footer_confirm_text"
        values={{ b: () => <strong>{projectName}</strong> }}
      />
    </Modal>
  );
};
